import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Popover,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { useParams } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import InstanceLeftSidebar from "../../InstanceLeftSidebar";
import {
  createWarehouse,
  fetchBinReceipts,
  fetchWarehouseLayout,
  fetchWarehouses,
  saveWarehouseLayout,
} from "../../../../services/shipping";

const DEFAULT_ROWS = 14;
const DEFAULT_COLS = 22;

const EDITOR_ROLES = new Set(["shipping-rep", "production-rep", "purchasing-rep", "administrator", "admin"]);

const TOOL_DEF = {
  select: { label: "Select", color: "#e5e7eb", text: "#111827" },
  rack: { label: "Rack", color: "#475569", text: "#ffffff" },
  bin: { label: "Bin", color: "#facc15", text: "#111827" },
  stairs: { label: "Stairs", color: "#f59e0b", text: "#111827" },
  staging: { label: "Staging", color: "#fdba74", text: "#111827" },
  packStation: { label: "Pack Station", color: "#1e3a8a", text: "#ffffff" },
  door: { label: "Door", color: "#334155", text: "#ffffff" },
  receiving: { label: "Receiving", color: "#4ade80", text: "#14532d" },
  shipping: { label: "Shipping", color: "#22d3ee", text: "#083344" },
  desk: { label: "Desk", color: "#6366f1", text: "#ffffff" },
};

const TOOL_KEYS = Object.keys(TOOL_DEF);
const getKey = (r, c) => `${r}:${c}`;
const CELL_SIZE = 30;
const CELL_GAP = 4;
const CELL_PITCH = CELL_SIZE + CELL_GAP;
const GRID_OVERSCAN = 4;
const STORAGE_WRITE_DEBOUNCE_MS = 350;

const defaultBounds = () => ({ minRow: 0, maxRow: DEFAULT_ROWS - 1, minCol: 0, maxCol: DEFAULT_COLS - 1 });

const blankLevel = (n = 1) => ({
  id: `L${n}`,
  name: `Level ${n}`,
  bounds: defaultBounds(),
  cells: {},
});

const emptyLayout = {
  levels: [blankLevel(1)],
  activeLevelId: "L1",
};

const parseQty = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.max(0, n) : 0;
};

const normalizeLevel = (level, index = 0) => ({
  id: level?.id || `L${index + 1}`,
  name: level?.name || `Level ${index + 1}`,
  bounds: {
    ...defaultBounds(),
    ...(level?.bounds || {}),
  },
  cells: level?.cells || {},
});

const normalizeLayout = (layout) => {
  const levelsRaw = Array.isArray(layout?.levels) && layout.levels.length ? layout.levels : [blankLevel(1)];
  const levels = levelsRaw.map((lvl, idx) => normalizeLevel(lvl, idx));
  return {
    levels,
    activeLevelId: layout?.activeLevelId || levels[0]?.id || "L1",
  };
};

const getRolesFromToken = () => {
  try {
    const token = localStorage.getItem("accessToken");
    if (!token) return [];
    const decoded = jwtDecode(token);
    return []
      .concat(decoded?.role || [])
      .concat(decoded?.roles || [])
      .concat(decoded?.userRole || [])
      .map((r) => String(r || "").toLowerCase())
      .filter(Boolean);
  } catch {
    return [];
  }
};

const WarehouseGridCell = React.memo(function WarehouseGridCell({
  gridRow,
  gridCol,
  row,
  col,
  cellKey,
  cell,
  selected,
  matchesSearch,
  canEdit,
  viewMode,
  dragSourceKey,
  onCellClick,
  onCellMouseEnter,
  onCellDragOver,
  onCellDrop,
  onDragStart,
  onRemove,
}) {
  const cfg = cell ? TOOL_DEF[cell.type] || TOOL_DEF.select : TOOL_DEF.select;

  return (
    <button
      type="button"
      onClick={(event) => onCellClick(gridRow, gridCol, event)}
      onMouseEnter={(event) => onCellMouseEnter(gridRow, gridCol, event)}
      onDragOver={(event) => onCellDragOver(event)}
      onDrop={(event) => onCellDrop(cellKey, event)}
      style={{
        position: "absolute",
        left: col,
        top: row,
        width: CELL_SIZE,
        height: CELL_SIZE,
        padding: 0,
        borderRadius: 4,
        border: selected ? "2px solid #ef4444" : matchesSearch ? "2px solid #10b981" : "1px solid #dbe3ee",
        backgroundColor: cell ? cfg.color : "#f8fafc",
        color: cell ? cfg.text : "#94a3b8",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: cell?.type === "bin" ? "pointer" : "default",
        boxSizing: "border-box",
        fontSize: 10,
        lineHeight: 1,
        userSelect: "none",
        outline: "none",
        boxShadow: "none",
      }}
    >
      {cell ? (
        <Box sx={{ position: "relative", width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
          {selected && canEdit && !viewMode && (
            <>
              <Box
                draggable
                onDragStart={(event) => onDragStart(cellKey, event)}
                onClick={(event) => event.stopPropagation()}
                sx={{
                  position: "absolute",
                  top: 1,
                  left: 1,
                  width: 14,
                  height: 14,
                  borderRadius: "50%",
                  backgroundColor: dragSourceKey === cellKey ? "rgba(17,24,39,0.8)" : "rgba(17,24,39,0.55)",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "grab",
                  zIndex: 2,
                  border: 0,
                }}
              >
                <DragIndicatorIcon sx={{ fontSize: 10 }} />
              </Box>

              <Box
                component="button"
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  onRemove(cellKey);
                }}
                sx={{
                  position: "absolute",
                  top: 1,
                  right: 1,
                  width: 14,
                  height: 14,
                  borderRadius: "50%",
                  backgroundColor: "rgba(153,27,27,0.85)",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  zIndex: 2,
                  border: 0,
                  p: 0,
                }}
              >
                <CloseIcon sx={{ fontSize: 10 }} />
              </Box>
            </>
          )}

          <Box component="span">{(cell.label || cfg.label).slice(0, 3)}</Box>
        </Box>
      ) : null}
    </button>
  );
});

export default function InstanceWarehouseBuilder() {
  const { instanceUniqueId } = useParams();

  const [layout, setLayout] = useState(emptyLayout);
  const [activeTool, setActiveTool] = useState("bin");
  const [selectedCellKey, setSelectedCellKey] = useState(null);
  const [search, setSearch] = useState("");
  const [saveState, setSaveState] = useState({ loading: false, message: "" });

  const [warehouses, setWarehouses] = useState([]);
  const [activeWarehouseKey, setActiveWarehouseKey] = useState("main");
  const [activeWarehouseName, setActiveWarehouseName] = useState("Main Warehouse");
  const [loadingWarehouse, setLoadingWarehouse] = useState(false);

  const [expandBy, setExpandBy] = useState(1);
  const [newWarehouseDialogOpen, setNewWarehouseDialogOpen] = useState(false);
  const [newWarehouseName, setNewWarehouseName] = useState("");

  const [viewMode, setViewMode] = useState(false);
  const [binPopover, setBinPopover] = useState({ anchorEl: null, title: "", loading: false, receipts: [] });
  const [dragSourceKey, setDragSourceKey] = useState(null);
  const binReceiptsCacheRef = useRef(new Map());
  const inFlightBinRequestsRef = useRef(new Map());
  const activePopoverKeyRef = useRef("");
  const gridContainerRef = useRef(null);
  const cellEditorRef = useRef(null);
  const persistTimeoutRef = useRef(null);
  const latestLayoutRef = useRef(layout);
  const [gridViewport, setGridViewport] = useState({
    scrollTop: 0,
    scrollLeft: 0,
    width: 0,
    height: 0,
  });

  const userRoles = useMemo(() => getRolesFromToken(), []);
  const canEdit = useMemo(() => userRoles.some((r) => EDITOR_ROLES.has(r)), [userRoles]);

  const storageKey = `warehouse-builder:v3:${instanceUniqueId}:${activeWarehouseKey}`;

  useEffect(() => {
    latestLayoutRef.current = layout;
  }, [layout]);

  useEffect(() => {
    if (persistTimeoutRef.current) clearTimeout(persistTimeoutRef.current);
    persistTimeoutRef.current = setTimeout(() => {
      localStorage.setItem(storageKey, JSON.stringify(latestLayoutRef.current));
    }, STORAGE_WRITE_DEBOUNCE_MS);

    return () => {
      if (persistTimeoutRef.current) clearTimeout(persistTimeoutRef.current);
    };
  }, [layout, storageKey]);

  useEffect(() => () => {
    if (persistTimeoutRef.current) {
      clearTimeout(persistTimeoutRef.current);
      localStorage.setItem(storageKey, JSON.stringify(latestLayoutRef.current));
    }
  }, [storageKey]);

  const loadWarehouses = useCallback(async () => {
    const res = await fetchWarehouses(instanceUniqueId);
    const list = Array.isArray(res?.warehouses) ? res.warehouses : [];
    setWarehouses(list);
    if (list.length && !list.some((w) => w.warehouseKey === activeWarehouseKey)) {
      setActiveWarehouseKey(list[0].warehouseKey);
      setActiveWarehouseName(list[0].name || "Warehouse");
    }
  }, [activeWarehouseKey, instanceUniqueId]);

  const loadActiveLayout = useCallback(async (warehouseKey) => {
    setLoadingWarehouse(true);
    try {
      const res = await fetchWarehouseLayout(instanceUniqueId, warehouseKey);
      const incomingLayout = res?.layout ? normalizeLayout(res.layout) : null;
      if (incomingLayout) {
        setLayout(incomingLayout);
      } else {
        const raw = localStorage.getItem(`warehouse-builder:v3:${instanceUniqueId}:${warehouseKey}`);
        if (raw) {
          setLayout(normalizeLayout(JSON.parse(raw)));
        } else {
          setLayout(emptyLayout);
        }
      }
      setActiveWarehouseName(res?.warehouse?.name || "Warehouse");
    } catch {
      const raw = localStorage.getItem(`warehouse-builder:v3:${instanceUniqueId}:${warehouseKey}`);
      if (raw) {
        setLayout(normalizeLayout(JSON.parse(raw)));
      } else {
        setLayout(emptyLayout);
      }
    } finally {
      setSelectedCellKey(null);
      setLoadingWarehouse(false);
    }
  }, [instanceUniqueId]);

  useEffect(() => {
    loadWarehouses();
  }, [loadWarehouses]);

  useEffect(() => {
    loadActiveLayout(activeWarehouseKey);
  }, [activeWarehouseKey, loadActiveLayout]);

  useEffect(() => {
    if (!canEdit) {
      setViewMode(true);
    }
  }, [canEdit]);

  const activeLevel = useMemo(
    () => layout.levels.find((l) => l.id === layout.activeLevelId) || layout.levels[0],
    [layout]
  );

  useEffect(() => {
    const gridEl = gridContainerRef.current;
    if (!gridEl) return undefined;

    const syncViewport = () => {
      setGridViewport({
        scrollTop: gridEl.scrollTop,
        scrollLeft: gridEl.scrollLeft,
        width: gridEl.clientWidth,
        height: gridEl.clientHeight,
      });
    };

    syncViewport();
    gridEl.addEventListener("scroll", syncViewport, { passive: true });
    const resizeObserver = new ResizeObserver(syncViewport);
    resizeObserver.observe(gridEl);

    return () => {
      gridEl.removeEventListener("scroll", syncViewport);
      resizeObserver.disconnect();
    };
  }, [activeWarehouseKey, activeLevel?.id, loadingWarehouse]);

  useEffect(() => {
    binReceiptsCacheRef.current.clear();
    activePopoverKeyRef.current = "";
    setBinPopover({ anchorEl: null, title: "", loading: false, receipts: [] });
  }, [instanceUniqueId, activeWarehouseKey, activeLevel?.id]);

  useEffect(() => {
    const handleDocumentMouseDown = (event) => {
      const gridEl = gridContainerRef.current;
      const editorEl = cellEditorRef.current;
      if (gridEl?.contains(event.target) || editorEl?.contains(event.target)) return;

      activePopoverKeyRef.current = "";
      setBinPopover({ anchorEl: null, title: "", loading: false, receipts: [] });
    };

    document.addEventListener("mousedown", handleDocumentMouseDown);
    return () => {
      document.removeEventListener("mousedown", handleDocumentMouseDown);
    };
  }, []);

  const closeBinPopover = useCallback(() => {
    activePopoverKeyRef.current = "";
    setBinPopover({ anchorEl: null, title: "", loading: false, receipts: [] });
  }, []);

  const updateActiveLevel = useCallback((updater) => {
    setLayout((currentLayout) => {
      const currentActiveLevel =
        currentLayout.levels.find((level) => level.id === currentLayout.activeLevelId) || currentLayout.levels[0];
      if (!currentActiveLevel) return currentLayout;

      const nextLevels = currentLayout.levels.map((level) => {
        if (level.id !== currentActiveLevel.id) return level;
        return updater(level);
      });

      return { ...currentLayout, levels: nextLevels };
    });
  }, []);

  const handleShowBinPopover = useCallback(async (anchorEl, r, c, cell) => {
    if (cell?.type !== "bin") return;
    const cellKey = getKey(r, c);
    const cacheKey = `${instanceUniqueId}:${activeWarehouseKey}:${activeLevel?.id}:${cellKey}`;
    const title = `${activeLevel?.name || activeLevel?.id || "Level"} · ${cell?.label || cellKey}`;
    const cached = binReceiptsCacheRef.current.get(cacheKey);

    if (activePopoverKeyRef.current === cacheKey) {
      return;
    }

    activePopoverKeyRef.current = cacheKey;

    if (cached) {
      setBinPopover({ anchorEl, title, loading: false, receipts: cached });
      return;
    }

    setBinPopover({ anchorEl, title, loading: true, receipts: [] });

    try {
      const existingRequest = inFlightBinRequestsRef.current.get(cacheKey);
      const requestPromise = existingRequest || fetchBinReceipts({
        instanceUniqueId,
        warehouseLevel: activeLevel?.id,
        warehouseCell: cellKey,
        warehouseKey: activeWarehouseKey,
      });

      if (!existingRequest) {
        inFlightBinRequestsRef.current.set(cacheKey, requestPromise);
      }

      const res = await requestPromise;
      const receipts = Array.isArray(res?.receipts) ? res.receipts : [];
      binReceiptsCacheRef.current.set(cacheKey, receipts);
      inFlightBinRequestsRef.current.delete(cacheKey);

      if (activePopoverKeyRef.current !== cacheKey) return;
      setBinPopover((prev) => ({
        ...prev,
        loading: false,
        receipts,
      }));
    } catch {
      inFlightBinRequestsRef.current.delete(cacheKey);
      if (activePopoverKeyRef.current !== cacheKey) return;
      setBinPopover((prev) => ({ ...prev, loading: false, receipts: [] }));
    }
  }, [instanceUniqueId, activeWarehouseKey, activeLevel?.id, activeLevel?.name]);

  const handleCellClick = useCallback((r, c, event) => {
    const key = getKey(r, c);
    const existing = activeLevel?.cells?.[key];
    const isPaintClick = Boolean(event?.shiftKey);

    // Keep click-to-open behavior lightweight: clicking existing cells should select/open,
    // not repaint/toggle the layout when a paint tool is active.
    if (canEdit && !viewMode && activeTool !== "select" && existing) {
      setSelectedCellKey(key);
      closeBinPopover();
      return;
    }

    if (viewMode || !canEdit) {
      setSelectedCellKey(key);
      if (existing?.type === "bin") {
        handleShowBinPopover(event.currentTarget, r, c, existing);
      } else {
        closeBinPopover();
      }
      return;
    }

    if (activeTool === "select" || !isPaintClick) {
      setSelectedCellKey(key);
      closeBinPopover();
      return;
    }

    updateActiveLevel((level) => {
      const current = level.cells[key];
      const nextCells = { ...level.cells };
      if (current?.type === activeTool) {
        delete nextCells[key];
      } else {
        const baseLabel = `${TOOL_DEF[activeTool].label}-${r + 1}-${c + 1}`;
        nextCells[key] = {
          type: activeTool,
          label: current?.label || baseLabel,
          sku: current?.sku || "",
          qty: current?.qty || 0,
          minLevel: current?.minLevel || 0,
          desc: current?.desc || "",
          coord: { x: c + 1, y: r + 1, z: Number(String(level.id).replace(/[^0-9]/g, "")) || 1 },
        };
      }
      return { ...level, cells: nextCells };
    });

    setSelectedCellKey(key);
  }, [activeLevel, activeTool, canEdit, closeBinPopover, handleShowBinPopover, viewMode, updateActiveLevel]);

  const handleCellMouseEnter = useCallback((r, c, event) => {
    if (!viewMode && canEdit) return;
    const key = getKey(r, c);
    const existing = activeLevel?.cells?.[key];
    if (existing?.type !== "bin") return;
    handleShowBinPopover(event.currentTarget, r, c, existing);
  }, [activeLevel, canEdit, handleShowBinPopover, viewMode]);

  const selectedCell = selectedCellKey ? activeLevel?.cells?.[selectedCellKey] || null : null;

  const updateSelectedCell = (field, value) => {
    if (!selectedCellKey || viewMode || !canEdit) return;
    updateActiveLevel((level) => {
      const current = level.cells[selectedCellKey];
      if (!current) return level;
      const nextCells = {
        ...level.cells,
        [selectedCellKey]: {
          ...current,
          [field]: value,
        },
      };
      return { ...level, cells: nextCells };
    });
  };

  const removeCellAtKey = useCallback((cellKey) => {
    if (!cellKey || viewMode || !canEdit) return;
    const targetCell = activeLevel?.cells?.[cellKey];
    if (!targetCell) return;
    const shouldDelete = window.confirm(`Remove ${targetCell.label || targetCell.type || "this item"} from ${cellKey}?`);
    if (!shouldDelete) return;

    updateActiveLevel((level) => {
      const nextCells = { ...level.cells };
      delete nextCells[cellKey];
      return { ...level, cells: nextCells };
    });

    if (selectedCellKey === cellKey) setSelectedCellKey(null);
  }, [activeLevel, canEdit, selectedCellKey, updateActiveLevel, viewMode]);

  const moveCell = useCallback((sourceKey, targetKey) => {
    if (!sourceKey || !targetKey || sourceKey === targetKey || viewMode || !canEdit) return;

    const sourceCell = activeLevel?.cells?.[sourceKey];
    if (!sourceCell) return;
    const targetCell = activeLevel?.cells?.[targetKey];

    const confirmText = targetCell
      ? `Move ${sourceCell.label || sourceCell.type || sourceKey} to ${targetKey}? This will replace ${targetCell.label || targetCell.type || targetKey}.`
      : `Move ${sourceCell.label || sourceCell.type || sourceKey} to ${targetKey}?`;

    const shouldMove = window.confirm(confirmText);
    if (!shouldMove) return;

    updateActiveLevel((level) => {
      const nextCells = { ...level.cells };
      const movingCell = nextCells[sourceKey];
      if (!movingCell) return level;

      const [targetRow, targetCol] = targetKey.split(":").map(Number);
      nextCells[targetKey] = {
        ...movingCell,
        coord: {
          ...(movingCell.coord || {}),
          x: Number.isFinite(targetCol) ? targetCol + 1 : movingCell?.coord?.x,
          y: Number.isFinite(targetRow) ? targetRow + 1 : movingCell?.coord?.y,
        },
      };
      delete nextCells[sourceKey];
      return { ...level, cells: nextCells };
    });

    if (selectedCellKey === sourceKey) setSelectedCellKey(targetKey);
  }, [activeLevel, canEdit, selectedCellKey, updateActiveLevel, viewMode]);

  const expandActiveLevel = (direction) => {
    if (viewMode || !canEdit) return;
    const steps = Math.max(1, Number(expandBy) || 1);
    updateActiveLevel((level) => {
      const bounds = { ...(level.bounds || defaultBounds()) };
      if (direction === "top") bounds.minRow -= steps;
      if (direction === "bottom") bounds.maxRow += steps;
      if (direction === "left") bounds.minCol -= steps;
      if (direction === "right") bounds.maxCol += steps;
      return { ...level, bounds };
    });
  };

  const addLevel = () => {
    if (viewMode || !canEdit) return;
    const nextNum = layout.levels.length + 1;
    const next = blankLevel(nextNum);
    setLayout((currentLayout) => ({
      ...currentLayout,
      levels: [...currentLayout.levels, next],
      activeLevelId: next.id,
    }));
    setSelectedCellKey(null);
  };

  const renameActiveLevel = (name) => {
    if (viewMode || !canEdit) return;
    updateActiveLevel((level) => ({ ...level, name }));
  };

  const handleSaveLayout = async () => {
    if (viewMode || !canEdit) return;
    setSaveState({ loading: true, message: "Saving..." });
    try {
      await saveWarehouseLayout(instanceUniqueId, layout, {
        warehouseKey: activeWarehouseKey,
        warehouseName: activeWarehouseName,
      });
      await loadWarehouses();
      setSaveState({ loading: false, message: "Layout saved" });
    } catch {
      setSaveState({ loading: false, message: "Save failed" });
    }
  };

  const handleCreateWarehouse = async () => {
    if (viewMode || !canEdit) return;
    const name = newWarehouseName.trim();
    if (!name) return;

    setSaveState({ loading: true, message: "Creating warehouse..." });
    try {
      const res = await createWarehouse(instanceUniqueId, {
        name,
        layout: emptyLayout,
      });
      const created = res?.warehouse;
      await loadWarehouses();
      if (created?.warehouseKey) {
        setActiveWarehouseKey(created.warehouseKey);
      }
      setNewWarehouseName("");
      setNewWarehouseDialogOpen(false);
      setSaveState({ loading: false, message: "Warehouse created" });
    } catch {
      setSaveState({ loading: false, message: "Warehouse creation failed" });
    }
  };

  const deferredSearch = React.useDeferredValue(search);

  const filteredRows = useMemo(() => {
    const q = deferredSearch.trim().toLowerCase();
    const rows = Object.entries(activeLevel?.cells || {}).map(([key, cell]) => ({
      key,
      ...cell,
      levelName: activeLevel?.name || "",
      levelId: activeLevel?.id || "",
    }));
    if (!q) return rows;
    return rows.filter((row) => {
      const hay = [row.key, row.label, row.desc, row.type].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [activeLevel, deferredSearch]);

  const totals = useMemo(() => {
    const allCells = layout.levels.flatMap((level) => Object.values(level.cells || {}));
    const totalBins = allCells.filter((cell) => cell?.type === "bin").length;
    const totalStairs = allCells.filter((cell) => cell?.type === "stairs").length;
    const receiving = allCells.filter((cell) => cell?.type === "receiving").length;
    const shipping = allCells.filter((cell) => cell?.type === "shipping").length;
    const desks = allCells.filter((cell) => cell?.type === "desk").length;
    return { totalBins, totalStairs, receiving, shipping, desks };
  }, [layout]);

  const isPopoverOpen = Boolean(binPopover.anchorEl);
  const searchNormalized = deferredSearch.trim().toLowerCase();
  const levelBounds = activeLevel?.bounds || defaultBounds();
  const totalGridRows = Math.max(0, levelBounds.maxRow - levelBounds.minRow + 1);
  const totalGridCols = Math.max(0, levelBounds.maxCol - levelBounds.minCol + 1);
  const virtualRowStart = Math.max(
    levelBounds.minRow,
    levelBounds.minRow + Math.floor(gridViewport.scrollTop / CELL_PITCH) - GRID_OVERSCAN
  );
  const virtualRowEnd = Math.min(
    levelBounds.maxRow,
    levelBounds.minRow + Math.ceil((gridViewport.scrollTop + gridViewport.height) / CELL_PITCH) + GRID_OVERSCAN
  );
  const virtualColStart = Math.max(
    levelBounds.minCol,
    levelBounds.minCol + Math.floor(gridViewport.scrollLeft / CELL_PITCH) - GRID_OVERSCAN
  );
  const virtualColEnd = Math.min(
    levelBounds.maxCol,
    levelBounds.minCol + Math.ceil((gridViewport.scrollLeft + gridViewport.width) / CELL_PITCH) + GRID_OVERSCAN
  );

  const visibleGridCells = useMemo(() => {
    const cells = [];
    for (let r = virtualRowStart; r <= virtualRowEnd; r += 1) {
      for (let c = virtualColStart; c <= virtualColEnd; c += 1) {
        const key = getKey(r, c);
        const cell = activeLevel?.cells?.[key];
        const matches = Boolean(
          searchNormalized &&
            [key, cell?.label, cell?.desc, cell?.type].filter(Boolean).join(" ").toLowerCase().includes(searchNormalized)
        );

        cells.push({
          id: key,
          key,
          row: r,
          col: c,
          left: (c - levelBounds.minCol) * CELL_PITCH,
          top: (r - levelBounds.minRow) * CELL_PITCH,
          cell,
          matchesSearch: matches,
        });
      }
    }
    return cells;
  }, [activeLevel, levelBounds.minCol, levelBounds.minRow, searchNormalized, virtualColEnd, virtualColStart, virtualRowEnd, virtualRowStart]);

  const handleCellDragOver = useCallback((event) => {
    if (!canEdit || viewMode || !dragSourceKey) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, [canEdit, dragSourceKey, viewMode]);

  const handleCellDrop = useCallback((targetKey, event) => {
    event.preventDefault();
    if (!canEdit || viewMode) return;
    const sourceKey = event.dataTransfer.getData("text/warehouse-cell") || dragSourceKey;
    setDragSourceKey(null);
    moveCell(sourceKey, targetKey);
  }, [canEdit, dragSourceKey, moveCell, viewMode]);

  const handleDragStart = useCallback((cellKey, event) => {
    event.stopPropagation();
    setDragSourceKey(cellKey);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/warehouse-cell", cellKey);
  }, []);

  return (
    <div className="instance-dashboard">
      <InstanceLeftSidebar />
      <div className="rightSide">
        <Box sx={{ p: 3 }}>
          <Typography variant="h4" sx={{ mb: 0.5 }}>Warehouse Builder</Typography>
          <Typography variant="body2" sx={{ opacity: 0.75, mb: 2 }}>
            Add/edit warehouses with infinitely expandable grid bounds and quick bin-content lookup for fast PO inventory checks.
          </Typography>

          {!canEdit && (
            <Alert severity="info" sx={{ mb: 2 }}>
              You are in <b>View Warehouse</b> mode. Hover/click bins for quick contents. Edit tools are restricted by role.
            </Alert>
          )}

          <Paper sx={{ p: 1.5, mb: 1.5 }}>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
              <FormControl size="small" sx={{ minWidth: 210 }}>
                <InputLabel id="warehouse-select-label">Warehouse</InputLabel>
                <Select
                  labelId="warehouse-select-label"
                  label="Warehouse"
                  value={activeWarehouseKey}
                  onChange={(e) => setActiveWarehouseKey(e.target.value)}
                >
                  {warehouses.map((warehouse) => (
                    <MenuItem key={warehouse.warehouseKey} value={warehouse.warehouseKey}>
                      {warehouse.name}
                    </MenuItem>
                  ))}
                  {!warehouses.length && <MenuItem value="main">Main Warehouse</MenuItem>}
                </Select>
              </FormControl>

              {canEdit && <Button variant="outlined" onClick={() => setNewWarehouseDialogOpen(true)}>+ New Warehouse</Button>}

              <TextField
                size="small"
                label="Warehouse Name"
                value={activeWarehouseName}
                onChange={(e) => setActiveWarehouseName(e.target.value)}
                sx={{ minWidth: 220 }}
                disabled={!canEdit || viewMode}
              />

              {canEdit && (
                <Button variant={viewMode ? "outlined" : "contained"} onClick={() => setViewMode((v) => !v)}>
                  {viewMode ? "Switch to Edit" : "Switch to View"}
                </Button>
              )}

              <Button variant="contained" onClick={handleSaveLayout} disabled={saveState.loading || viewMode || !canEdit}>
                {saveState.loading ? "Saving…" : "Save Layout"}
              </Button>
            </Stack>

            {!!saveState.message && (
              <Typography variant="caption" sx={{ mt: 1, display: "block", opacity: 0.75 }}>
                {saveState.message}
              </Typography>
            )}
          </Paper>

          <Paper sx={{ p: 1.5, mb: 1.5 }}>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
              {TOOL_KEYS.map((toolKey) => {
                const cfg = TOOL_DEF[toolKey];
                const active = activeTool === toolKey;
                return (
                  <Button
                    key={toolKey}
                    onClick={() => setActiveTool(toolKey)}
                    variant={active ? "contained" : "outlined"}
                    disabled={viewMode || !canEdit}
                    sx={{
                      textTransform: "none",
                      borderColor: cfg.color,
                      color: active ? cfg.text : cfg.color,
                      backgroundColor: active ? cfg.color : "transparent",
                    }}
                  >
                    {cfg.label}
                  </Button>
                );
              })}

              <Divider orientation="vertical" flexItem />

              <FormControl size="small" sx={{ minWidth: 170 }}>
                <InputLabel id="wh-level-label">Level</InputLabel>
                <Select
                  labelId="wh-level-label"
                  label="Level"
                  value={layout.activeLevelId}
                  onChange={(e) => {
                    setSelectedCellKey(null);
                    setLayout((currentLayout) => ({ ...currentLayout, activeLevelId: e.target.value }));
                  }}
                >
                  {layout.levels.map((level) => (
                    <MenuItem key={level.id} value={level.id}>{level.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button variant="outlined" onClick={addLevel} disabled={viewMode || !canEdit}>+ Add Level</Button>

              <TextField
                size="small"
                label="Level Name"
                value={activeLevel?.name || ""}
                onChange={(e) => renameActiveLevel(e.target.value)}
                disabled={viewMode || !canEdit}
              />

              <TextField
                size="small"
                type="number"
                label="Expand by"
                value={expandBy}
                onChange={(e) => setExpandBy(Math.max(1, Number(e.target.value) || 1))}
                sx={{ width: 110 }}
                disabled={viewMode || !canEdit}
              />

              <Button variant="outlined" onClick={() => expandActiveLevel("top")} disabled={viewMode || !canEdit}>+ Top</Button>
              <Button variant="outlined" onClick={() => expandActiveLevel("bottom")} disabled={viewMode || !canEdit}>+ Bottom</Button>
              <Button variant="outlined" onClick={() => expandActiveLevel("left")} disabled={viewMode || !canEdit}>+ Left</Button>
              <Button variant="outlined" onClick={() => expandActiveLevel("right")} disabled={viewMode || !canEdit}>+ Right</Button>
            </Stack>
            {canEdit && !viewMode && (
              <Typography variant="caption" sx={{ mt: 0.8, display: "block", opacity: 0.72 }}>
                Click selects/opens bin details. Hold <b>Shift</b> and click to paint/remove using the active tool.
              </Typography>
            )}
          </Paper>

          <Paper ref={gridContainerRef} sx={{ p: 1.5, mb: 2, overflow: "auto" }}>
            {loadingWarehouse ? (
              <Alert severity="info">Loading warehouse layout…</Alert>
            ) : (
              <Box
                sx={{
                  position: "relative",
                  width: Math.max(900, totalGridCols * CELL_PITCH),
                  height: Math.max(420, totalGridRows * CELL_PITCH),
                  background: "linear-gradient(#f8fafc,#eef2f7)",
                  p: 1,
                  borderRadius: 1,
                  border: "1px solid #dbe3ee",
                  boxSizing: "border-box",
                }}
              >
                {visibleGridCells.map((gridCell) => (
                  <WarehouseGridCell
                    key={gridCell.id}
                    gridRow={gridCell.row}
                    gridCol={gridCell.col}
                    row={gridCell.top}
                    col={gridCell.left}
                    cellKey={gridCell.key}
                    cell={gridCell.cell}
                    selected={selectedCellKey === gridCell.key}
                    matchesSearch={gridCell.matchesSearch}
                    canEdit={canEdit}
                    viewMode={viewMode}
                    dragSourceKey={dragSourceKey}
                    onCellClick={handleCellClick}
                    onCellMouseEnter={handleCellMouseEnter}
                    onCellDragOver={handleCellDragOver}
                    onCellDrop={handleCellDrop}
                    onDragStart={handleDragStart}
                    onRemove={removeCellAtKey}
                  />
                ))}
              </Box>
            )}
          </Paper>

          <Stack direction={{ xs: "column", lg: "row" }} spacing={2}>
            <Paper
              ref={cellEditorRef}
              onMouseDown={(event) => event.stopPropagation()}
              onClick={(event) => event.stopPropagation()}
              sx={{ p: 1.5, flex: 1 }}
            >
              <Typography variant="subtitle1" sx={{ mb: 1 }}>Cell Editor</Typography>
              <Typography variant="caption" sx={{ display: "block", mb: 1, opacity: 0.75 }}>
                Selected: {selectedCellKey || "None"}
              </Typography>

              {!selectedCell ? (
                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                  Pick a cell to edit label and location notes.
                </Typography>
              ) : (
                <Stack spacing={1}>
                  <Chip size="small" label={`Type: ${selectedCell.type || "unknown"}`} sx={{ width: "fit-content" }} />
                  <TextField size="small" label="Label" value={selectedCell.label || ""} onChange={(e) => updateSelectedCell("label", e.target.value)} disabled={viewMode || !canEdit} />
                  <TextField size="small" multiline minRows={2} label="Description" value={selectedCell.desc || ""} onChange={(e) => updateSelectedCell("desc", e.target.value)} disabled={viewMode || !canEdit} />
                  {selectedCell.type === "bin" && (
                    <Typography variant="caption" sx={{ opacity: 0.72 }}>
                      Goods stored in this bin are managed through shipping receipts and bin assignments, not this layout editor.
                    </Typography>
                  )}
                </Stack>
              )}
            </Paper>

            <Paper sx={{ p: 1.5, flex: 1 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>View Warehouse / Search</Typography>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 1 }}>
                <TextField
                  size="small"
                  label="Search cells or descriptions"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  fullWidth
                />
                <Chip label={`Bins ${totals.totalBins}`} color="warning" variant="outlined" />
                <Chip label={`Stairs ${totals.totalStairs}`} color="warning" variant="outlined" />
              </Stack>

              <Box sx={{ display: "flex", gap: 1, mb: 1, flexWrap: "wrap" }}>
                <Chip size="small" label={`Receiving ${totals.receiving}`} />
                <Chip size="small" label={`Shipping ${totals.shipping}`} />
                <Chip size="small" label={`Desks ${totals.desks}`} />
              </Box>

              <TableContainer sx={{ maxHeight: 360 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Cell</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Label</TableCell>
                      <TableCell>Description</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredRows.map((row) => (
                      <TableRow
                        key={row.key}
                        hover
                        onClick={() => setSelectedCellKey(row.key)}
                        sx={{ cursor: "pointer" }}
                      >
                        <TableCell>{row.key}</TableCell>
                        <TableCell>{row.type || "—"}</TableCell>
                        <TableCell>{row.label || "—"}</TableCell>
                        <TableCell>{row.desc || "—"}</TableCell>
                      </TableRow>
                    ))}
                    {!filteredRows.length && (
                      <TableRow>
                        <TableCell colSpan={4} align="center">No cells found.</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Stack>
        </Box>
      </div>

      <Dialog open={newWarehouseDialogOpen} onClose={() => setNewWarehouseDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Create Warehouse</DialogTitle>
        <DialogContent>
          <TextField
            sx={{ mt: 1 }}
            size="small"
            label="Warehouse Name"
            value={newWarehouseName}
            onChange={(e) => setNewWarehouseName(e.target.value)}
            fullWidth
            placeholder="e.g. Promo Showroom"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewWarehouseDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateWarehouse}>Create</Button>
        </DialogActions>
      </Dialog>

      <Popover
        sx={{ pointerEvents: "none" }}
        open={isPopoverOpen}
        anchorEl={binPopover.anchorEl}
        onClose={(_, reason) => {
          if (reason === "backdropClick") return;
          closeBinPopover();
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transitionDuration={{ appear: 160, enter: 160, exit: 120 }}
        PaperProps={{
          sx: { pointerEvents: "none" },
        }}
      >
        <Box sx={{ p: 1.25, minWidth: 320, maxWidth: 420 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.8 }}>{binPopover.title || "Bin"}</Typography>
          {binPopover.loading ? (
            <Typography variant="body2" sx={{ opacity: 0.7 }}>Loading bin contents…</Typography>
          ) : !binPopover.receipts.length ? (
            <Typography variant="body2" sx={{ opacity: 0.7 }}>No PO contents saved for this bin yet.</Typography>
          ) : (
            <Stack spacing={0.75}>
              {binPopover.receipts.slice(0, 8).map((receipt) => (
                <Paper key={receipt.id} variant="outlined" sx={{ p: 0.8 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {receipt.goodsName || "Unnamed goods"}
                  </Typography>
                  <Typography variant="caption" sx={{ display: "block", opacity: 0.8 }}>
                    PO {receipt.poNumber || receipt.poId} · Box {receipt.boxRef || "UNTRACKED"} · Qty {receipt.quantity || 0} {receipt.unit || "pcs"}{receipt.binSubLocation ? ` · Slot ${receipt.binSubLocation}` : ""}{receipt.binShelfLevel ? ` · Shelf ${receipt.binShelfLevel}` : ""}
                  </Typography>
                </Paper>
              ))}
            </Stack>
          )}
        </Box>
      </Popover>
    </div>
  );
}
